import React, { Component } from 'react'
import withRouter from 'with-router'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import PropTypes from 'prop-types';
import CircularProgress from '@mui/material/CircularProgress';
import Fade from '@mui/material/Fade';
import classNames from 'classnames';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDownOutlined';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUpOutlined';
import AddOutlined from '@mui/icons-material/AddOutlined';
import RemoveOutlined from '@mui/icons-material/RemoveOutlined';
import { Trans, withTranslation, useTranslation } from 'react-i18next';
import { withSnackbar } from '../utils/withSnackbar';
import store from '../store';



import Logo from '../containers/design/logo-icon.svg?react';
import { Box, Button, Collapse } from '@mui/material';


/****
 *
 * ERROR LEVELS
 *
 * 0 => Show Whoops
 * 1 => Show Whoops + Close currently active/open Item.
 * 2 => Show Whoops + Close open Subfields section and leave the active Item opened.
 * 3 => No Whoops and show the error in a Snackbar
 *
 *
 * props.progress default is an int value between 0 and 100 to represent the progress in percent.
 * alternatively it can be an object with the following structure, when there is an unknown amount of total fields:
 * {
 *    value,    // Int
 *    perPage,  // Items per page
 *    page,     // The current loaded page
 *    type: 'loading' | 'saving' | 'fields_found' | 'fields_loaded' |
 * }
 */

const stylesObj = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    // Inline-Style, daher über die globale CSS-Variable statt über das Theme
    backgroundColor: 'var(--ly-backdrop)',
    zIndex:10,
  },
  button: (theme) => ({
    margin: theme.spacing(2),
  }),
  placeholder: {
    textAlign: 'center',
  },
  // a soft shade behind the text only (the logo carries its own colour), fading out well inside
  // the element so no edge shows: the overlay alone leaves the text hard to read over busy content,
  // in dark mode especially. No backdrop blur: its effect stops at the box and reads as one
  // block, not inline-block: the logo is an inline span, an inline text box would sit beside it
  textShade: {
    display: 'block',
    width: 'fit-content',
    padding: '20px 72px',
    margin: '-20px auto',
    background: 'radial-gradient(ellipse at center, var(--ly-loading-shade) 0%, transparent 70%)',
  },
  logoWrapper: {
    position: 'relative',
    width: '225px',
    height: '60px',
    marginBottom: '24px',
  },
  logoLayer1: {
    position: 'absolute',
    backgroundImage: 'url("/langify-logo.png")',
    backgroundPosition: 'center center',
    backgroundSize: 'contain',
    width: '100%',
    height: '100%',
    opacity: .45,
  },
  logoLayer2: {
    position: 'absolute',
    backgroundImage: 'url("/langify-logo.png")',
    backgroundPosition: 'center center',
    backgroundSize: 'contain',
    width: '100%',
    height: '100%',
    opacity: 0,
  },
  circularProgress: {
    color: 'var(--ly-loading-icon)',
    margin: '24px'
  },
};

class LoadingIndicator extends Component {

  constructor(props) {
    super(props);

    const t = props.t;

    this.state = {
      loadingStr: '',
      loadingDots: '',
      loadingType: (typeof props.progress === 'object') ? props.progress.type : props.loadingType,
      showError: false,
      showErrorDetail: false,
    }
  }

  componentDidMount() {
    const { t } = this.props;
    let tick = 0;
    this.clock = setInterval(() => {
      this.setState({
        loadingStr: this.renderLoadingText(),
        loadingDots: '.'.repeat(tick),
      })
      tick += 1;
      if(tick == 5) tick = 0;

      //console.log(`${this.renderLoadingText()}${'.'.repeat(tick)}${'\u00A0'.repeat(4-tick)}`)
      //this.forceUpdate();
    }, 500)
  }

  componentWillUnmount() {
    clearInterval(this.clock);
  }

  componentWillReceiveProps(newProps) {
    const { t } = this.props;
    if(this.props.error === false && typeof newProps.error === 'object') {
      if(newProps.error?.level === 3) {
        this.props.enqueueSnackbar(newProps.error.message, {
          variant: 'error',
          persist: true,
          action: (key) => <Button size='small' onClick={() => this.props.closeSnackbar(key)} style={{ color: 'white' }}>
            {t('system.close')}
          </Button>
        });
      }
      else {
        this.setState({ showError: true });
      }
    }
  }

  renderLoadingText = () => {
    const { t, progress, loadingType } = this.props;
    let loadingText = '';

    if (progress !== undefined && progress !== null) {
      if (typeof progress === 'object' && 'value' in progress && 'type' in progress) {
        loadingText = `${progress.value} ${t('system.' + progress.type)}`;
      } else if (typeof progress === 'number' ) {
        loadingText = `${progress ? progress + '% ' : ''}${loadingType ? t('system.' + loadingType) : t('system.loading')}`;
      } else {
        console.warn('Invalid progress value:', progress);
        loadingText = t('system.loading'); // Fallback for unexpected types
      }
    } else {
      // no progress to report, but the caller may still know what is running
      loadingText = loadingType ? t('system.' + loadingType) : t('system.loading');
    }

    return loadingText;
  }

  handleErrorCancel = () => {
    this.setState({
      showError: false,
    });
    if(this.props.onErrorCancel) {
      this.props.onErrorCancel();
    }
  }

  handleToggleErrorDetail = () => {
    this.setState({
      showErrorDetail: !this.state.showErrorDetail,
    });
  }

  handleErrorRetry = () => {
    this.setState({
      showError: false,
    });
    if(this.props.onErrorRetry) {
      this.props.onErrorRetry();
    }
    if(typeof this.props.error.action === 'function') {
      store.dispatch(this.props.error.action());
    }
  }

  render() {
    // the logo spinner is the default everywhere; only the splash screen, which draws the full
    // logo itself, opts out with withLogo={false}
    const { t, styles, pending, progress, withLogo = true, message, error } = this.props;
    const { showError, showErrorDetail } = this.state;

    return (
      <React.Fragment>
        <Fade
          in={pending || showError}
          style={{
            transitionDelay: pending ? '0ms' : '0ms',
          }}
          unmountOnExit
        >
          <div id="loadingIndicator" className={classNames(this.props.loadingType === 'initializing' ? 'initializing' : null)} style={this.props.loadingType === 'initializing' ? undefined : stylesObj.root}>
            <div style={stylesObj.placeholder}>
              <Fade
                in={pending || showError}
                style={{
                  transitionDelay: pending ? '400ms' : '0ms',
                }}
                unmountOnExit
              >
                {!showError ?
                  <div>
                    {withLogo ?
                      <Logo />
                    :
                      <CircularProgress sx={(styles && styles.circularProgress) ? styles.circularProgress : stylesObj.circularProgress} />
                    }

                    <div style={this.props.loadingType === 'initializing' ? undefined : stylesObj.textShade}>
                      {/* the one text below the logo: a caller's message, else the default with its
                          dots hanging off the word's right edge out of the flow (padded into the
                          text they pushed the word two characters left of centre) */}
                      <Typography variant="subtitle1" sx={{ color: 'text.primary', marginTop: '16px', position: 'relative', display: 'inline-block' }}>
                        {message ?? this.state.loadingStr}
                        {!message &&
                          <Box component="span" sx={{ position: 'absolute', left: '100%', top: 0, whiteSpace: 'pre' }}>{this.state.loadingDots}</Box>
                        }
                      </Typography>
                    </div>
                  </div>
                :
                  <div>
                    <Typography variant="h4" color="primary" style={{marginBottom: 24}}>
                      {t('system.whoopsMessage')}
                    </Typography>
                    {this.props.error.level !== 0 &&
                      <Button
                        variant="outlined"
                        size="large"
                        onClick={this.handleErrorCancel}
                        style={{marginRight: 8}}>
                        {t('system.cancel')}
                      </Button>
                    }
                    <Button variant="contained" size="large"
                      color="primary"
                      onClick={this.handleErrorRetry}
                    >
                      {t('system.tryAgain')} <RefreshIcon style={{marginLeft: 5}} />
                    </Button>
                    {this.props.error.message && this.props.error.message.length > 0 &&
                      <div style={{marginTop: 24, marginBottom: 24}}>
                        <Typography variant="overline" onClick={this.handleToggleErrorDetail} style={{cursor: 'pointer'}}>
                          {!showErrorDetail ? 'show' : 'hide' } error details {/*!showErrorDetail ? <AddOutlined size={6} /> : <RemoveOutlined size={6} /> */}
                        </Typography>
                        <Collapse in={showErrorDetail}>
                          <Typography variant="body2" style={{marginTop: 8}}>
                            { Array.isArray(this.props.error.message) ?
                              this.props.error.message[0].message
                            :
                              this.props.error.message
                            }
                          </Typography>
                        </Collapse>
                      </div>
                    }
                  </div>
                }

              </Fade>
            </div>
          </div>
        </Fade>
      </React.Fragment>
    );
  }
}

export default withTranslation()(withSnackbar(LoadingIndicator));
